/**
 * Centralized Environment Configuration
 *
 * All environment-specific URLs and settings are DERIVED from a single set of
 * per-env PRIMITIVES (DOMAIN_MODEL §c.5), injected by the central domain-config
 * generator into the pod env and bridged to the client by RuntimeConfig.tsx:
 *
 *   PRIMARY_DOMAIN            the apex / browser BASE_DOMAIN (everything is <prefix>.<this>)
 *   COOKIE_DOMAIN            explicit leading-dot cookie domain (NOT always .PRIMARY_DOMAIN)
 *   ROUTED_ALTERNATE_DOMAINS  CSV of routed-alternate apexes (full host + CORS + redirect)
 *   TRUST_DOMAINS             CSV of CORS/CSRF/cookie/redirect trust-only apexes
 *   PUBLIC_CORS_ORIGINS       CSV of allowed origins
 *   CANONICAL_REDIRECT        "true" => canonicalize routed alternates -> primary
 *
 * There are NO hardcoded apex literals here: a base of `demo.engageiq.ca`
 * produces correct hosts (https://demo.engageiq.ca, https://api-supabase.demo.engageiq.ca,
 * cookie .demo.engageiq.ca) exactly as `demo.neutralaiz.com` / `portal.neutralaiz.com`
 * do. Public service URLs are <scheme>://<prefix>.<PRIMARY_DOMAIN> (apex => <scheme>://<PRIMARY_DOMAIN>),
 * with prefixes matching k8s/domain-config/services.yaml.
 *
 * Usage:
 *   import { env } from '@/lib/config/environment';
 *   const url = env.urls.fileBrowser; // https://file-browser.{BASE_DOMAIN}
 */

// =============================================================================
// Core Domain Configuration
// =============================================================================

/**
 * Default base domain — SINGLE FALLBACK, env-supplied only (DOMAIN_MODEL §d.3).
 * No apex literal is baked in: we read it from the build/runtime env so ONE image
 * works for every domain. If genuinely nothing is set (local dev with no env), we
 * leave it empty and callers derive from window.location as a last resort.
 */
const DEFAULT_BASE_DOMAIN =
  process.env.NEXT_PUBLIC_DEFAULT_BASE_DOMAIN ||
  process.env.NEXT_PUBLIC_BASE_DOMAIN ||
  process.env.BASE_DOMAIN ||
  '';

/**
 * Get the canonical PRIMARY domain at runtime.
 *
 * Per DOMAIN_MODEL §c.5 the frontend derives from the CANONICAL domain primitive
 * (the pod's PRIMARY_DOMAIN / BASE_DOMAIN), NOT from window.location parsing — so
 * a routed-alternate host (e.g. engageiq.neutralaiz.com) still derives every URL
 * against the primary (demo.engageiq.ca) and canonicalizes correctly.
 *
 * Priority:
 *  1. window.__RUNTIME_CONFIG__.NEXT_PUBLIC_PRIMARY_DOMAIN (server-injected primitive)
 *  2. window.__RUNTIME_CONFIG__.NEXT_PUBLIC_BASE_DOMAIN    (server-injected base)
 *  3. process.env.PRIMARY_DOMAIN / BASE_DOMAIN / NEXT_PUBLIC_BASE_DOMAIN (SSR/build)
 *  4. window.location.hostname (last-resort client fallback when no primitive set)
 *  5. DEFAULT_BASE_DOMAIN
 */
function getBaseDomain(): string {
  if (typeof window !== 'undefined') {
    const rc = window.__RUNTIME_CONFIG__;
    const fromConfig =
      rc?.NEXT_PUBLIC_PRIMARY_DOMAIN || rc?.NEXT_PUBLIC_BASE_DOMAIN;
    if (fromConfig) {
      return fromConfig;
    }
  }
  // Server-side or build-time primitive
  const fromEnv =
    process.env.PRIMARY_DOMAIN ||
    process.env.BASE_DOMAIN ||
    process.env.NEXT_PUBLIC_BASE_DOMAIN;
  if (fromEnv) {
    return fromEnv;
  }
  // Last-resort client fallback: use the current host so the app still functions
  // when no primitive was injected (e.g. local dev). This is the ONLY place
  // window.location is consulted, and only as a fallback.
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return DEFAULT_BASE_DOMAIN;
}

/**
 * Canonical frontend/auth base. Services use this same family unless the CPU
 * profile explicitly supplies its separate technical service domain.
 */
export const BASE_DOMAIN = getBaseDomain();

// Explicit CPU deployments may serve the app on a separate public origin while
// retaining their same-VPS technical host family. Other profiles keep one base.
function getServiceBaseDomain(): string {
  const runtime = typeof window !== 'undefined'
    ? window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_SERVICE_BASE_DOMAIN : undefined;
  const configured = runtime || process.env.SERVICE_BASE_DOMAIN || process.env.NEXT_PUBLIC_SERVICE_BASE_DOMAIN;
  if (!configured) return getBaseDomain();
  const domain = configured.trim().toLowerCase();
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(domain)) {
    throw new Error('Invalid configured service base domain');
  }
  return domain;
}
const SERVICE_BASE_DOMAIN = getServiceBaseDomain();


/**
 * Per-ENVIRONMENT Supabase auth storage key.
 *
 * The session's cookie/localStorage key MUST be namespaced per environment.
 * `staging.engageiq.neutralaiz.com` is a SUBDOMAIN of the prod apex
 * `engageiq.neutralaiz.com`, so a shared fixed name (`sb-neutralaiz-auth-token`)
 * written by prod on `.engageiq.neutralaiz.com` leaks DOWN to staging. The
 * browser then hands staging a session minted by a DIFFERENT Supabase project ->
 * GoTrue "Invalid Refresh Token: Refresh Token Not Found" -> the
 * clear-cookies -> `/login?message=Session refreshed` LOOP.
 *
 * Keying by BASE_DOMAIN (the app's single source of truth for hosts, derived
 * IDENTICALLY on client and server — unlike the Supabase URL, which differs
 * external vs internal and is why the old code used one fixed constant) makes
 * each env's key distinct, so a leaked cross-subdomain cookie is simply ignored.
 * The `sb-neutralaiz-auth-token` PREFIX is preserved so the existing
 * prefix-based cookie cleanup / size checks keep matching. (2026-08-03)
 */
export function getAuthStorageKey(): string {
  return authStorageKeyForDomain(getBaseDomain());
}

/**
 * The storage key for a GIVEN domain. This is the one derivation the browser
 * client (via getAuthStorageKey() after RuntimeConfig rewrote BASE_DOMAIN to
 * the routed domain the page is on) and every server-side reader (middleware,
 * server client, /auth/callback — via resolveRequestAuthStorageKey()) must
 * share. On a routed ALTERNATE (prod engageiq.neutralaiz.com) the browser keys
 * the session off the alternate; a server keying off the static primary looked
 * for a cookie that never existed, bounced every page to /login and broke the
 * PKCE exchange (2026-09-14).
 */
export function authStorageKeyForDomain(domain: string | null | undefined): string {
  const base = (domain || 'default')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `sb-neutralaiz-auth-token-${base}`;
}

/**
 * Parse a CSV primitive (ROUTED_ALTERNATE_DOMAINS / TRUST_DOMAINS) into a
 * trimmed, deduped list, honoring runtime config first then process.env.
 */
function getDomainList(runtimeKey: string, envKey: string): string[] {
  let raw = '';
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.[runtimeKey]) {
    raw = window.__RUNTIME_CONFIG__[runtimeKey];
  } else {
    raw = process.env[runtimeKey] || process.env[envKey] || '';
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((d, i, arr) => arr.indexOf(d) === i);
}

/**
 * Routed-alternate apexes (DOMAIN_MODEL §c.5): apexes that get FULL per-svc host
 * derivation + CORS + redirect (active-active). Empty in the 4 non-cloud envs;
 * cloud carries `engageiq.neutralaiz.com`.
 */
export function getRoutedAlternateDomains(): string[] {
  return getDomainList(
    'NEXT_PUBLIC_ROUTED_ALTERNATE_DOMAINS',
    'ROUTED_ALTERNATE_DOMAINS'
  );
}

/**
 * Trust-only apexes (DOMAIN_MODEL §c.5): appear ONLY in CORS/CSRF/cookie/redirect
 * allow-lists — never routed, never a cert SAN, never a per-svc host. This is the
 * prod `neutralaiz.com` apex case (cookie/CORS trust boundary, no per-svc routing).
 */
export function getTrustDomains(): string[] {
  return getDomainList('NEXT_PUBLIC_TRUST_DOMAINS', 'TRUST_DOMAINS');
}

/**
 * Whether routed alternates should 301-canonicalize to the primary (cookie/OAuth).
 */
export function getCanonicalRedirect(): boolean {
  if (
    typeof window !== 'undefined' &&
    window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_CANONICAL_REDIRECT
  ) {
    return window.__RUNTIME_CONFIG__.NEXT_PUBLIC_CANONICAL_REDIRECT === 'true';
  }
  return (
    (process.env.CANONICAL_REDIRECT ||
      process.env.NEXT_PUBLIC_CANONICAL_REDIRECT) === 'true'
  );
}

/**
 * Explicit COOKIE_DOMAIN primitive (leading dot). NOT always `.PRIMARY_DOMAIN`:
 * prod widens to the apex trust boundary (e.g. `.neutralaiz.com`). Returns the
 * raw primitive when set, else null so derivation falls back to `.BASE_DOMAIN`.
 */
function getCookieDomainPrimitive(): string | null {
  if (
    typeof window !== 'undefined' &&
    window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_COOKIE_DOMAIN
  ) {
    return window.__RUNTIME_CONFIG__.NEXT_PUBLIC_COOKIE_DOMAIN;
  }
  return process.env.COOKIE_DOMAIN || process.env.NEXT_PUBLIC_COOKIE_DOMAIN || null;
}

/**
 * Cookie domain for cross-subdomain auth.
 * Derived from the COOKIE_DOMAIN primitive when present (prod widens to apex);
 * otherwise `.{BASE_DOMAIN}` (e.g. demo.engageiq.ca -> .demo.engageiq.ca).
 */
export const COOKIE_DOMAIN = (() => {
  const explicit = getCookieDomainPrimitive();
  if (explicit) return explicit;
  return `.${BASE_DOMAIN}`;
})();

const ENVIRONMENT_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  cloud: 'cloud',
  demo: 'demo',
  denvr: 'denvr',
  dev: 'development',
  development: 'development',
  prod: 'production',
  production: 'production',
  staging: 'staging',
  test: 'test',
});

function normalizeEnvironment(value: string | undefined): string | null {
  const normalized = value?.trim().toLowerCase();
  return normalized ? ENVIRONMENT_ALIASES[normalized] || null : null;
}

/**
 * Resolve the explicit application/settings environment before considering
 * the hostname. DEPLOY_ENV is a database key, not a presentation label: the
 * demo3/demo4 physical hosts both intentionally use the shared `demo` rows.
 * Runtime config leads build-time values so one frontend image remains
 * portable between environments. Unknown values are ignored rather than
 * selecting an arbitrary platform_settings namespace.
 */
function getExplicitEnvironment(): string | null {
  const candidates = [
    typeof window !== 'undefined'
      ? window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_DEPLOY_ENV
      : undefined,
    process.env.DEPLOY_ENV,
    process.env.NEXT_PUBLIC_DEPLOY_ENV,
    process.env.ENVIRONMENT,
  ];
  for (const candidate of candidates) {
    const environment = normalizeEnvironment(candidate);
    if (environment) return environment;
  }
  return null;
}

/**
 * Detect current environment name from explicit deployment identity first,
 * then from the primary domain's leading label as a compatibility fallback.
 * Generic hostname inference works for any apex, including demo.engageiq.ca:
 *   - apex with no env-style first label => 'production'
 *   - first label one of staging/demo/denvr/dev/test => that env
 *   - otherwise 'production' (custom apex like demo.engageiq.ca, portal.*)
 */
export const ENVIRONMENT = (() => {
  const explicit = getExplicitEnvironment();
  if (explicit) return explicit;

  const firstLabel = BASE_DOMAIN.split('.')[0] || '';
  // Local dev / unset / bare single-label host (localhost, '', an IP) is NEVER
  // production — classifying it so makes getSupabaseUrl() refuse the derived
  // self-hosted fallback and breaks local/Playwright dev with no primitives.
  const labelCount = BASE_DOMAIN.split('.').filter(Boolean).length;
  if (labelCount <= 1) return 'development';
  // A two-label apex (e.g. `engageiq.ca`, `neutralaiz.com`) or a non-env first
  // label (e.g. `portal`) is treated as production.
  if (labelCount <= 2) return 'production';
  return normalizeEnvironment(firstLabel) || 'production';
})();

/**
 * Detect if we're running in a production-class environment.
 */
export const IS_PRODUCTION = ENVIRONMENT === 'production';

// =============================================================================
// Service registry (mirror of k8s/domain-config/services.yaml prefixes + schemes)
// =============================================================================

/**
 * Public service registry — prefix + scheme per service, MATCHING
 * k8s/domain-config/services.yaml (the GLOBAL source of truth). Empty prefix =
 * apex app. `publicServiceUrl(name)` synthesizes <scheme>://<prefix>.<PRIMARY_DOMAIN>.
 *
 * Keep these prefixes/schemes in sync with services.yaml. The `name` keys match
 * the registry `name` field; `envKeys` are documented in services.yaml.
 */
const SERVICE_REGISTRY = {
  app: { prefix: '', scheme: 'https' },
  'api-supabase': { prefix: 'api-supabase', scheme: 'https' },
  'supabase-studio': { prefix: 'supabase', scheme: 'https' },
  livekit: { prefix: 'livekit', scheme: 'wss' },
  'livekit-turn': { prefix: 'livekit-turn', scheme: 'turns' },
  canvas: { prefix: 'canvas', scheme: 'https' },
  maps: { prefix: 'maps', scheme: 'https' },
  'context-engine': { prefix: 'context-engine', scheme: 'wss' },
  'file-browser': { prefix: 'file-browser', scheme: 'https' },
  reports: { prefix: 'reports', scheme: 'https' },
  vectoraiz: { prefix: 'vectoraiz', scheme: 'https' },
  'ai-engine': { prefix: 'ai-engine', scheme: 'https' },
  agent: { prefix: 'agent', scheme: 'https' },
  mediaaiz: { prefix: 'mediaaiz', scheme: 'https' },
  recognaiz: { prefix: 'recognaiz', scheme: 'https' },
  chunkaiz: { prefix: 'chunkaiz', scheme: 'https' },
  extractaiz: { prefix: 'extractaiz', scheme: 'https' },
  trace: { prefix: 'trace', scheme: 'https' },
} as const;

export type ServiceName = keyof typeof SERVICE_REGISTRY;

/**
 * host(svc, D) — D when prefix=="" (apex), else `<prefix>.<D>`.
 */
function serviceHost(prefix: string, domain: string): string {
  return prefix ? `${prefix}.${domain}` : domain;
}

/**
 * publicServiceUrl(name) — DOMAIN_MODEL §c.5:
 *   `${scheme}://${host(svc, PRIMARY_DOMAIN)}`  (apex prefix => `${scheme}://${PRIMARY_DOMAIN}`)
 * Derives against the CANONICAL primary domain (not window.location), so routed
 * alternates canonicalize to the primary for the app.
 */
export function publicServiceUrl(name: ServiceName): string {
  const svc = SERVICE_REGISTRY[name];
  const primary = name === 'app' ? getBaseDomain() : getServiceBaseDomain();
  return `${svc.scheme}://${serviceHost(svc.prefix, primary)}`;
}

// =============================================================================
// Service URLs - All derived from BASE_DOMAIN via the service registry
// =============================================================================

/**
 * All service URLs, derived from the single PRIMARY domain.
 * These replace hardcoded URLs throughout the codebase.
 */
export const urls = {
  // Frontend & Main App — both the apex app (services.yaml `app`, empty prefix).
  frontend: publicServiceUrl('app'),
  app: publicServiceUrl('app'),

  // Supabase (self-hosted: api-supabase.{primary} pattern with DASHES)
  supabaseApi: publicServiceUrl('api-supabase'),
  supabaseStudio: publicServiceUrl('supabase-studio'),

  // LiveKit
  livekit: publicServiceUrl('livekit'),
  livekitHttp: `https://${serviceHost(SERVICE_REGISTRY.livekit.prefix, SERVICE_BASE_DOMAIN)}`,

  // Collaborative Tools
  canvas: publicServiceUrl('canvas'),
  maps: publicServiceUrl('maps'),
  fileBrowser: publicServiceUrl('file-browser'),

  // AI & Engine Services
  aiEngine: publicServiceUrl('ai-engine'),
  aiEngineV1: `${publicServiceUrl('ai-engine')}/v1`,
  aiEngineDocs: `${publicServiceUrl('ai-engine')}/docs`,
  aiEngineApiDocs: `${publicServiceUrl('ai-engine')}/api-docs`,
  // Legacy "engine-demo" playground host (not in the central registry; out of
  // scope of §c.5). Derive its single prefix from BASE_DOMAIN — no apex literal.
  aiEngineDemo: `https://${serviceHost('engine-demo', SERVICE_BASE_DOMAIN)}`,

  // Reports
  reports: publicServiceUrl('reports'),

  // Agent Services
  // agentCaller: historical "caller." host (services.yaml routes the agent backend
  // as `agent.<P>`; the dead `caller.` derivation is retained only for back-compat).
  agentCaller: `https://${serviceHost('caller', SERVICE_BASE_DOMAIN)}`,
  // agentBackend: the app's own backend proxy path on the apex app (NO `demo.` prefix).
  agentBackend: `${publicServiceUrl('app')}/backend-api`,

  // Search & RAG (not in the central registry; derive single prefixes from BASE_DOMAIN)
  realtimeSearchApi: `https://${serviceHost('realtime-search-api', SERVICE_BASE_DOMAIN)}`,
  realtimeSearchElement: `https://${serviceHost('realtime-search', SERVICE_BASE_DOMAIN)}/element/realtime-search-element.umd.js`,

  // Context Engine (WebSocket)
  contextEngine: publicServiceUrl('context-engine'),

  // VectorAiz
  vectoraiz: publicServiceUrl('vectoraiz'),

  // Portal (legacy single-prefix host; derive from BASE_DOMAIN)
  portal: `https://${serviceHost('portal', SERVICE_BASE_DOMAIN)}`,

  // Documentation (not in the central registry; derive from BASE_DOMAIN)
  docs: `https://${serviceHost('docs', SERVICE_BASE_DOMAIN)}`,
} as const;

// =============================================================================
// Internal Service URLs (for server-side, container-to-container)
// =============================================================================

/**
 * Internal URLs for server-side container communication.
 * These use Docker service names and are not accessible from browser.
 */
export const internalUrls = {
  fileBrowser:
    process.env.FILE_BROWSER_URL ||
    'http://neutralaiz-ui-file-browser:8080',
  canvas:
    process.env.CANVAS_URL ||
    'http://neutralaiz-ui-canvas:8080',
  aiEngine:
    process.env.AI_ENGINE_URL ||
    'http://neutralaiz-api-ai-engine:8080',
  reports:
    process.env.REPORTS_URL ||
    'http://neutralaiz-api-reports:8080',
  agentBackend:
    process.env.AGENT_BACKEND_URL ||
    'http://neutralaiz-agent-backend:8080',
  supabase:
    process.env.SUPABASE_URL ||
    'http://neutralaiz-infra-supabase-kong:8000',
  livekit:
    process.env.LIVEKIT_INTERNAL_URL ||
    'ws://neutralaiz-infra-livekit-server:7880',
} as const;

// =============================================================================
// URL Helper Functions
// =============================================================================

/**
 * Get the public URL for a service, preferring env var if set.
 * Falls back to the BASE_DOMAIN-derived URL.
 */
export function getPublicUrl(
  service: keyof typeof urls,
  envVarName?: string
): string {
  if (envVarName) {
    const envValue = process.env[envVarName];
    if (envValue) return envValue;
  }
  return urls[service];
}

/**
 * Get file browser URL (public).
 * HOST-AWARE: prefer the RuntimeConfig-injected NEXT_PUBLIC_FILE_BROWSER_URL, which
 * RuntimeConfig.tsx rewrites to the routed domain the page is on. This is read via
 * getRuntimeConfig (window.__RUNTIME_CONFIG__ first) — NOT a bare process.env.NEXT_PUBLIC_*
 * reference (which Next.js webpack/SWC would inline at build time to the static primary).
 * Falls back to the BASE_DOMAIN-derived urls.fileBrowser (itself host-aware via
 * getBaseDomain -> window.__RUNTIME_CONFIG__.NEXT_PUBLIC_PRIMARY_DOMAIN).
 */
export function getFileBrowserUrl(): string {
  return getRuntimeConfig('NEXT_PUBLIC_FILE_BROWSER_URL') || urls.fileBrowser;
}

/**
 * Get canvas URL (public).
 * HOST-AWARE: prefer the RuntimeConfig-injected value (window.__RUNTIME_CONFIG__),
 * which RuntimeConfig.tsx rewrites to the routed domain the page is actually on,
 * BEFORE the build-inlined process.env.NEXT_PUBLIC_* (always the static primary).
 * SSR/no-window: getRuntimeConfig falls back to process.env, then urls.canvas.
 */
export function getCanvasUrl(): string {
  return getRuntimeConfig('NEXT_PUBLIC_CANVAS_URL') || urls.canvas;
}

/**
 * Get maps URL (public). Key is NEXT_PUBLIC_MAP_URL (SINGULAR — services.yaml maps.envKeys).
 * HOST-AWARE via RuntimeConfig (see getCanvasUrl); SSR falls back to process.env then urls.maps.
 */
export function getMapsUrl(): string {
  return getRuntimeConfig('NEXT_PUBLIC_MAP_URL') || urls.maps;
}

/**
 * Get LiveKit WebSocket URL (public).
 */
export function getLiveKitUrl(): string {
  return (
    process.env.NEXT_PUBLIC_LIVEKIT_URL ||
    process.env.LIVEKIT_SELF_HOSTED_URL ||
    urls.livekit
  );
}

/**
 * Get LiveKit HTTP URL (public).
 */
export function getLiveKitHttpUrl(): string {
  return (
    process.env.LIVEKIT_SELF_HOSTED_HTTP_URL ||
    urls.livekitHttp
  );
}

/**
 * Get AI Engine URL (public).
 * AI_ENGINE_URL (server-only, non-public) stays highest priority; then HOST-AWARE
 * RuntimeConfig for NEXT_PUBLIC_AI_ENGINE_URL (routed-domain aware) before the
 * build-inlined process.env; SSR falls back to urls.aiEngine.
 */
export function getAiEngineUrl(): string {
  return (
    process.env.AI_ENGINE_URL ||
    getRuntimeConfig('NEXT_PUBLIC_AI_ENGINE_URL') ||
    urls.aiEngine
  );
}

/**
 * Get reports URL (public).
 * HOST-AWARE via RuntimeConfig (see getCanvasUrl); SSR falls back to process.env then urls.reports.
 */
export function getReportsUrl(): string {
  return getRuntimeConfig('NEXT_PUBLIC_REPORTS_URL') || urls.reports;
}

/**
 * Get Supabase API URL (public).
 * Cloud-Supabase environments must supply NEXT_PUBLIC_SUPABASE_URL (or the
 * server-injected SUPABASE_PUBLIC_URL/SUPABASE_URL) — only self-hosted envs fall
 * back to the derived api-supabase.{domain} pattern.
 */
export function getSupabaseUrl(): string {
  // Explicit env var always wins
  if (process.env.NEXT_PUBLIC_SUPABASE_URL) {
    return process.env.NEXT_PUBLIC_SUPABASE_URL;
  }

  // Cloud-Supabase environments must NOT fall back to the self-hosted URL pattern.
  // These require NEXT_PUBLIC_SUPABASE_URL to be set (at build or runtime).
  // NOTE: 'demo' uses self-hosted Supabase (supabase-selfhosted K8s component).
  const cloudEnvs = ['production', 'staging', 'test'];
  if (cloudEnvs.includes(ENVIRONMENT)) {
    // Check runtime config (server-injected)
    if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_SUPABASE_URL) {
      return window.__RUNTIME_CONFIG__.NEXT_PUBLIC_SUPABASE_URL;
    }
    // Fallback to server-side env vars. Only the EXTERNAL SUPABASE_PUBLIC_URL is
    // safe in any context. The internal SUPABASE_URL (in-cluster Kong) is
    // server-reachable ONLY — never return it to the browser (unroutable +
    // mixed-content). Non-NEXT_PUBLIC_ env is already absent from the client
    // bundle, but guard explicitly so a future refactor can't leak it.
    if (process.env.SUPABASE_PUBLIC_URL) return process.env.SUPABASE_PUBLIC_URL;
    if (typeof window === 'undefined' && process.env.SUPABASE_URL) return process.env.SUPABASE_URL;
    console.error(`[Env] Missing Supabase URL for cloud environment "${ENVIRONMENT}". Set NEXT_PUBLIC_SUPABASE_URL.`);
    return '';
  }

  return urls.supabaseApi;
}

/**
 * Get context engine WebSocket URL.
 * HOST-AWARE via RuntimeConfig (see getCanvasUrl): the wss host must match the routed
 * domain the page is on so the cross-origin WS handshake + auth cookie work; SSR falls
 * back to process.env then urls.contextEngine.
 */
export function getContextEngineUrl(): string {
  return getRuntimeConfig('NEXT_PUBLIC_CONTEXT_ENGINE_URL') || urls.contextEngine;
}

/**
 * Check if the realtime search service is enabled in this environment.
 * Returns true only if NEXT_PUBLIC_REALTIME_SEARCH_ENABLED is explicitly set to "true".
 * This prevents the search page from trying to load scripts from non-existent
 * realtime-search hostnames (which causes ERR_CERT_AUTHORITY_INVALID errors,
 * including on other pages like /reports due to Next.js route prefetching).
 */
export function isRealtimeSearchEnabled(): boolean {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_REALTIME_SEARCH_ENABLED) {
    return window.__RUNTIME_CONFIG__.NEXT_PUBLIC_REALTIME_SEARCH_ENABLED === 'true';
  }
  return process.env.NEXT_PUBLIC_REALTIME_SEARCH_ENABLED === 'true';
}

/**
 * Get realtime search API URL.
 * HOST-AWARE via RuntimeConfig (see getCanvasUrl); SSR falls back to process.env then derived URL.
 */
export function getRealtimeSearchApiUrl(): string {
  return getRuntimeConfig('NEXT_PUBLIC_REALTIME_SEARCH_API_URL') || urls.realtimeSearchApi;
}

/**
 * Get realtime search element script URL.
 * HOST-AWARE via RuntimeConfig (see getCanvasUrl); SSR falls back to process.env then derived URL.
 */
export function getRealtimeSearchElementUrl(): string {
  return (
    getRuntimeConfig('NEXT_PUBLIC_REALTIME_SEARCH_ELEMENT_URL') ||
    urls.realtimeSearchElement
  );
}

// =============================================================================
// Runtime Config Helper
// =============================================================================

/**
 * Get a NEXT_PUBLIC_* env var with runtime config priority.
 * RuntimeConfig.tsx injects ALL NEXT_PUBLIC_* vars into window.__RUNTIME_CONFIG__
 * at request time, overriding stale build-time values.
 *
 * Priority: window.__RUNTIME_CONFIG__ > process.env > defaultValue
 *
 * @param key - Full env var name (e.g., 'NEXT_PUBLIC_LIVEKIT_URL')
 * @param defaultValue - Fallback value
 */
export function getRuntimeConfig(key: string, defaultValue = ''): string {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.[key]) {
    return window.__RUNTIME_CONFIG__[key];
  }
  return process.env[key] ?? defaultValue;
}

// =============================================================================
// Cookie Domain Helpers
// =============================================================================

/**
 * Get cookie domain for the current environment.
 * Used for cross-subdomain session sharing. Derived from the COOKIE_DOMAIN
 * primitive (prod widens to the apex trust boundary) or `.{PRIMARY_DOMAIN}`.
 *
 * Guard: an EXPLICIT cookie domain that is BROADER than the current primary
 * (i.e. the primary does not end with it) is only honored when it matches a
 * declared TRUST_DOMAIN — otherwise it is a cross-environment leak and we fall
 * back to `.{PRIMARY_DOMAIN}`. This replaces the old hardcoded ".neutralaiz.com"
 * rejection with a generic, primitive-driven check (works for demo.engageiq.ca).
 */
export function getCookieDomain(): string {
  const baseDomain = getBaseDomain();
  const explicit = getCookieDomainPrimitive();

  if (explicit) {
    const stripped = explicit.replace(/^\./, '');
    // If the explicit domain is the primary (or a parent the primary lives under),
    // it's safe. e.g. primary demo.engageiq.ca + cookie .demo.engageiq.ca, or
    // prod primary portal.neutralaiz.com + cookie .neutralaiz.com (apex trust).
    const coversPrimary =
      baseDomain === stripped || baseDomain.endsWith(`.${stripped}`);
    if (coversPrimary) {
      // PER-ENV ISOLATION: a cookie domain BROADER than the primary (e.g. the
      // shared .neutralaiz.com apex) would send this env's auth cookie to sibling
      // environments. Only an explicit domain that EXACTLY equals the primary is
      // honored; anything broader falls back to `.{primary}`. (Routed alternates
      // get their per-request cookie domain from the request-host resolver, not
      // this static value.)
      if (baseDomain !== stripped) {
        console.error(
          `[Env] COOKIE_DOMAIN "${explicit}" is broader than BASE_DOMAIN "${baseDomain}". ` +
          `Ignoring to prevent cross-environment cookie leakage. Using ".${baseDomain}" instead.`
        );
        return `.${baseDomain}`;
      }
      return explicit;
    }
    // Explicit cookie domain doesn't cover the current primary at all — almost
    // certainly a stale/cross-env value; derive from the primary instead.
    console.error(
      `[Env] COOKIE_DOMAIN "${explicit}" does not cover BASE_DOMAIN "${baseDomain}". ` +
      `Ignoring to prevent cross-environment cookie leakage. Using ".${baseDomain}" instead.`
    );
    return `.${baseDomain}`;
  }

  // No explicit primitive: derive from the primary domain.
  return `.${baseDomain}`;
}

/**
 * Check if a hostname belongs to the current environment (primary, routed
 * alternates, or trust domains). Primitive-driven — no hardcoded apex.
 */
export function isCurrentEnvironment(hostname: string): boolean {
  const baseDomain = getBaseDomain();
  const families = [baseDomain, ...getRoutedAlternateDomains(), ...getTrustDomains()];
  return families.some((d) => hostname === d || hostname.endsWith(`.${d}`));
}

/**
 * Get all valid cookie domains for clearing cookies within the CURRENT environment.
 * PER-ENV ISOLATION: clears ONLY this env's own cookie domain (`.{primary}`) — it
 * must NOT expire a broader shared apex cookie (that would log the user out of the
 * sibling environments that share the apex).
 */
export function getAllCookieDomains(): string[] {
  return [getCookieDomain()];
}

// =============================================================================
// Default Export - Combined Environment Object
// =============================================================================

/**
 * Complete environment configuration object.
 * Import as: import { env } from '@/lib/config/environment';
 */
export const env = {
  // Core
  baseDomain: BASE_DOMAIN,
  cookieDomain: COOKIE_DOMAIN,
  environment: ENVIRONMENT,
  isProduction: IS_PRODUCTION,

  // Primitives (DOMAIN_MODEL §c.5)
  getRoutedAlternateDomains,
  getTrustDomains,
  getCanonicalRedirect,
  publicServiceUrl,

  // URLs
  urls,
  internalUrls,

  // Helpers
  getFileBrowserUrl,
  getCanvasUrl,
  getMapsUrl,
  getLiveKitUrl,
  getLiveKitHttpUrl,
  getAiEngineUrl,
  getReportsUrl,
  getSupabaseUrl,
  getContextEngineUrl,
  isRealtimeSearchEnabled,
  getRealtimeSearchApiUrl,
  getRealtimeSearchElementUrl,
  getCookieDomain,
  isCurrentEnvironment,
  getAllCookieDomains,
  getRuntimeConfig,
} as const;

export default env;
